import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { notificationAPI } from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [wsConnection, setWsConnection] = useState(null);
  const [deletedNotificationIds, setDeletedNotificationIds] = useState(() => {
    // Initialize from localStorage to persist across sessions
    // Also clean up entries older than 30 days to prevent unbounded growth
    try {
      const stored = localStorage.getItem('deletedNotificationIds');
      const deletedIdsData = stored ? JSON.parse(stored) : {};
      
      // If old format (array), convert to new format (object with timestamps)
      if (Array.isArray(deletedIdsData)) {
        const now = Date.now();
        const newFormat = {};
        deletedIdsData.forEach(id => {
          newFormat[id] = now;
        });
        localStorage.setItem('deletedNotificationIds', JSON.stringify(newFormat));
        return new Set(deletedIdsData);
      }
      
      // Clean up entries older than 30 days
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      const cleanedData = {};
      const activeIds = [];
      
      Object.entries(deletedIdsData).forEach(([id, timestamp]) => {
        if (timestamp > thirtyDaysAgo) {
          cleanedData[id] = timestamp;
          activeIds.push(id);
        }
      });
      
      // Save cleaned data back to localStorage
      localStorage.setItem('deletedNotificationIds', JSON.stringify(cleanedData));
      return new Set(activeIds);
      
    } catch {
      return new Set();
    }
  });

  // WebSocket connection management
  const connectWebSocket = useCallback(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
      const token = localStorage.getItem('auth_token');
      
      const ws = new WebSocket(`${wsUrl}/api/notifications/ws?token=${token}`);
      
      ws.onopen = () => {
        console.log('Notification WebSocket connected');
        setIsConnected(true);
        
        // Send ping to keep connection alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000); // 30 seconds
        
        ws.pingInterval = pingInterval;
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      ws.onclose = () => {
        console.log('Notification WebSocket disconnected');
        setIsConnected(false);
        
        if (ws.pingInterval) {
          clearInterval(ws.pingInterval);
        }
        
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          if (isAuthenticated) {
            connectWebSocket();
          }
        }, 5000);
      };
      
      ws.onerror = (error) => {
        console.error('Notification WebSocket error:', error);
        setIsConnected(false);
      };
      
      setWsConnection(ws);
      
    } catch (error) {
      console.error('Error connecting to notification WebSocket:', error);
    }
  }, [isAuthenticated, user?.id]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'notification':
        // New notification received
        const newNotification = data.data;
        setNotifications(prev => [newNotification, ...prev]);
        setUnreadCount(prev => prev + 1);
        setTotalCount(prev => prev + 1);
        
        // Show browser notification if permission granted
        showBrowserNotification(newNotification);
        break;
        
      case 'notification_counts':
        // Updated counts received
        setUnreadCount(data.data.unread);
        setTotalCount(data.data.total);
        break;
        
      case 'connection_established':
        console.log('Notification connection established');
        break;
        
      case 'pong':
        // Connection is alive
        break;
        
      default:
        console.log('Unknown notification message type:', data.type);
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((notification) => {
    // Check if browser notifications are supported and permitted
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      try {
        const browserNotif = new Notification(notification.title, {
          body: notification.message,
          icon: '/assets/tribal-root-logo.png',
          badge: '/assets/tribal-root-logo.png',
          tag: notification.id, // Prevent duplicate notifications
          requireInteraction: notification.priority === 'urgent'
        });
        
        browserNotif.onclick = () => {
          window.focus();
          browserNotif.close();
          
          // Handle action data if present
          if (notification.action_data) {
            handleNotificationClick(notification);
          }
        };
        
        // Auto-close after 5 seconds unless urgent
        if (notification.priority !== 'urgent') {
          setTimeout(() => browserNotif.close(), 5000);
        }
        
      } catch (error) {
        console.error('Error showing browser notification:', error);
      }
    }
  }, []);

  // Handle notification click actions
  const handleNotificationClick = useCallback((notification) => {
    const actionData = notification.action_data;
    
    if (actionData?.action === 'view_profile' && actionData?.user_id) {
      window.location.href = `/public-profile/${actionData.user_id}`;
    } else if (actionData?.conversation_id) {
      // Navigate to conversation
      window.location.href = `/messages/${actionData.conversation_id}`;
    }
  }, []);

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        return permission === 'granted';
      } catch (error) {
        console.error('Error requesting notification permission:', error);
        return false;
      }
    }
    return typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted';
  }, []);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (options = {}) => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      const response = await notificationAPI.getNotifications({
        limit: options.limit || 50,
        offset: options.offset || 0,
        status: options.status
      });

      // Filter out locally deleted notifications
      const filteredNotifications = response.notifications.filter(
        notif => !deletedNotificationIds.has(notif.id)
      );

      if (options.append) {
        setNotifications(prev => [...prev, ...filteredNotifications]);
      } else {
        setNotifications(filteredNotifications);
      }
      
      // Adjust counts to reflect locally deleted notifications
      const deletedNotifications = response.notifications.filter(
        notif => deletedNotificationIds.has(notif.id)
      );
      const deletedUnreadCount = deletedNotifications.filter(notif => notif.status === 'unread').length;
      
      setUnreadCount(Math.max(0, response.unread_count - deletedUnreadCount));
      setTotalCount(Math.max(0, response.total_count - deletedNotificationIds.size));
      
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, deletedNotificationIds]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationAPI.markAsRead(notificationId);
      
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, status: 'read', read_at: new Date().toISOString() }
            : notif
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationAPI.markAllAsRead();
      
      setNotifications(prev => 
        prev.map(notif => ({ 
          ...notif, 
          status: 'read', 
          read_at: new Date().toISOString() 
        }))
      );
      
      setUnreadCount(0);
      
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }, []);

  // Delete notification with optimistic updates
  const deleteNotification = useCallback(async (notificationId) => {
    // Find the notification before deletion for potential restoration
    const deletedNotif = notifications.find(n => n.id === notificationId);
    if (!deletedNotif) {
      console.warn('Notification not found for deletion:', notificationId);
      return;
    }

    // Optimistically update the UI immediately
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
    setTotalCount(prev => Math.max(0, prev - 1));
    
    if (deletedNotif.status === 'unread') {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    // Track deleted notification PERMANENTLY with timestamp
    setDeletedNotificationIds(prev => {
      const newSet = new Set([...prev, notificationId]);
      
      // Update localStorage with timestamp
      try {
        const stored = localStorage.getItem('deletedNotificationIds');
        const deletedIdsData = stored ? JSON.parse(stored) : {};
        deletedIdsData[notificationId] = Date.now();
        localStorage.setItem('deletedNotificationIds', JSON.stringify(deletedIdsData));
      } catch (error) {
        console.error('Error updating deleted notifications in localStorage:', error);
      }
      
      return newSet;
    });

    try {
      // Attempt API call in background to delete from server
      await notificationAPI.deleteNotification(notificationId);
      console.log('Notification deleted successfully from server:', notificationId);
      
      // Keep notification ID in deletedNotificationIds permanently to prevent re-appearance
      
    } catch (error) {
      console.warn('Failed to delete notification from server (kept in persistent local storage):', error);
      
      // Notification remains deleted in UI due to persistent tracking
      
      // For now, we'll keep the optimistic update even if API fails
      // This prevents CORS issues from blocking the user experience
    }
  }, [notifications]);

  // Get notification counts
  const fetchCounts = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const counts = await notificationAPI.getCounts();
      setUnreadCount(counts.unread);
      setTotalCount(counts.total);
    } catch (error) {
      console.error('Error fetching notification counts:', error);
    }
  }, [isAuthenticated]);

  // Create test notification (for development)
  const createTestNotification = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      await notificationAPI.createTestNotification();
    } catch (error) {
      console.error('Error creating test notification:', error);
    }
  }, [isAuthenticated]);

  // Safely request notification permission (only if supported)
  const safeRequestNotificationPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      console.log('Browser notifications not supported');
      return false;
    }
    
    try {
      return await requestNotificationPermission();
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [requestNotificationPermission]);

  // Clean up successfully deleted notifications from server
  const cleanupDeletedNotifications = useCallback(async () => {
    if (deletedNotificationIds.size === 0) return;
    
    console.log(`Attempting to sync ${deletedNotificationIds.size} locally deleted notifications with server...`);
    
    const successfullyDeleted = [];
    for (const notificationId of deletedNotificationIds) {
      try {
        await notificationAPI.deleteNotification(notificationId);
        successfullyDeleted.push(notificationId);
        console.log(`Successfully deleted notification ${notificationId} from server`);
      } catch (error) {
        console.warn(`Failed to delete notification ${notificationId} from server:`, error);
        // Keep in localStorage for future retry
      }
    }
    
    // Remove successfully deleted IDs from localStorage
    if (successfullyDeleted.length > 0) {
      setDeletedNotificationIds(prev => {
        const newSet = new Set(prev);
        successfullyDeleted.forEach(id => newSet.delete(id));
        localStorage.setItem('deletedNotificationIds', JSON.stringify([...newSet]));
        return newSet;
      });
      console.log(`Cleaned up ${successfullyDeleted.length} notifications from local storage`);
    }
  }, [deletedNotificationIds]);

  // Initialize notifications when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
      fetchCounts();
      cleanupDeletedNotifications(); // Try to sync deleted notifications with server
      connectWebSocket();
      safeRequestNotificationPermission();
    } else {
      // Clear notifications when user logs out
      setNotifications([]);
      setUnreadCount(0);
      setTotalCount(0);
      setIsConnected(false);
      // Keep deleted IDs persistent across logout/login sessions
      // Only clear when notifications are successfully deleted from server
      
      if (wsConnection) {
        wsConnection.close();
        setWsConnection(null);
      }
    }
  }, [isAuthenticated, fetchNotifications, fetchCounts, cleanupDeletedNotifications, connectWebSocket, safeRequestNotificationPermission]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsConnection) {
        wsConnection.close();
      }
    };
  }, [wsConnection]);

  const value = {
    notifications,
    unreadCount,
    totalCount,
    isConnected,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchCounts,
    createTestNotification,
    requestNotificationPermission: safeRequestNotificationPermission
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};